/**
 * Pure logic for the "choose an organisation" dialog on the All Users page,
 * used by Approve, Add user and the Choose organisation fix-up.
 *
 * KEEP IN SYNC with functions/src/orgs/orgAssignmentRules.ts. That copy is the
 * one the server enforces; this one only drives the preselection, the warning
 * and whether the button is enabled. functions/src/orgs/orgAssignmentRules.test.ts
 * checks that the two agree on a shared fixture.
 */

export interface OrgOption {
  orgId: string;
  name?: string;
  domains?: string[];
  status?: string;
}

/** Lower-cased domain after the last "@", or null when there is none. */
export function emailDomain(email: unknown): string | null {
  if (typeof email !== 'string') return null;
  const trimmed = email.trim().toLowerCase();
  const at = trimmed.lastIndexOf('@');
  if (at < 0) return null;
  const domain = trimmed.slice(at + 1).trim();
  return domain ? domain : null;
}

function isActive(status: unknown): boolean {
  return status === undefined || status === null || status === 'active';
}

/** True when the email's domain is one of the organisation's domains. */
export function orgDomainMatches(email: unknown, domains: unknown): boolean {
  const domain = emailDomain(email);
  if (!domain || !Array.isArray(domains)) return false;
  return domains
    .filter((d): d is string => typeof d === 'string')
    .map((d) => d.trim().toLowerCase())
    .includes(domain);
}

/** Every active organisation whose domains include the email's domain. */
export function suggestOrgIdsForEmail(email: unknown, orgs: ReadonlyArray<OrgOption>): string[] {
  return orgs.filter((o) => isActive(o.status) && orgDomainMatches(email, o.domains)).map((o) => o.orgId);
}

/** The organisations the picker may offer: active ones only. */
export function selectableOrgs<T extends OrgOption>(orgs: ReadonlyArray<T>): T[] {
  return orgs.filter((o) => isActive(o.status));
}

/** Preselect only when exactly one active organisation matches the domain. */
export function defaultOrgChoice(email: unknown, orgs: ReadonlyArray<OrgOption>): string {
  const matches = suggestOrgIdsForEmail(email, orgs);
  return matches.length === 1 ? matches[0] : '';
}

export interface OrgChoiceVerdict {
  /** Whether the dialog's confirm button may be pressed. */
  canSubmit: boolean;
  /** Whether to show the "email domain is not one of its domains" warning. */
  domainMismatch: boolean;
  /** The flag to send to the server; true only for an acknowledged mismatch. */
  overrideDomainMismatch: boolean;
}

export function evaluateOrgChoice(args: {
  email: string;
  orgId: string;
  orgs: ReadonlyArray<OrgOption>;
  acknowledged: boolean;
}): OrgChoiceVerdict {
  const org = selectableOrgs(args.orgs).find((o) => o.orgId === args.orgId);
  if (!args.orgId || !org) {
    return { canSubmit: false, domainMismatch: false, overrideDomainMismatch: false };
  }
  const domainMismatch = !orgDomainMatches(args.email, org.domains);
  if (!domainMismatch) {
    return { canSubmit: true, domainMismatch: false, overrideDomainMismatch: false };
  }
  return {
    canSubmit: args.acknowledged,
    domainMismatch: true,
    overrideDomainMismatch: args.acknowledged,
  };
}

/** What the Organisation column shows for an account. */
export function organisationLabel(orgId: string | undefined, orgs: ReadonlyArray<OrgOption>): string {
  if (!orgId) return 'No organisation';
  return orgs.find((o) => o.orgId === orgId)?.name || orgId;
}

export type OrganisationCell =
  | { kind: 'member'; label: string }
  | { kind: 'suggested'; label: string }
  | { kind: 'needs-finish'; label: string }
  | { kind: 'none'; label: string };

/**
 * What the All Users "Organisation" column shows.
 *   member        the sign-in claim names an organisation (shown by the
 *                 claim's name, even if users.orgId says something else), or a
 *                 member row exists for an account that is not approved yet
 *   needs-finish  approved, a member row exists, the claim is missing: a
 *                 half-done assignment; offer to finish it
 *   suggested     users.orgId with no member row and no claim: the sign-up
 *                 domain SUGGESTION, not membership (an approved account is
 *                 offered "Choose organisation", suggestion preselected)
 *   none          no organisation at all
 */
export function organisationCell(
  u: { status?: string; orgId?: string; claimOrgId?: string | null; memberRowInUserOrg?: boolean },
  orgs: ReadonlyArray<OrgOption>,
): OrganisationCell {
  // The sign-in claim IS the membership, whatever users.orgId says.
  if (u.claimOrgId) return { kind: 'member', label: organisationLabel(u.claimOrgId, orgs) };
  if (!u.orgId) return { kind: 'none', label: 'No organisation' };
  const name = organisationLabel(u.orgId, orgs);
  // A member row with no claim: a half-done assignment. Only an approved
  // account is offered "finish"; approving a pending one finishes it anyway.
  if (u.memberRowInUserOrg) {
    return u.status === 'approved' ? { kind: 'needs-finish', label: name } : { kind: 'member', label: name };
  }
  // No member row and no claim: the sign-up suggestion, not membership.
  return { kind: 'suggested', label: `Suggested: ${name}` };
}
